<template>
  <SrpDetailsSummary class="photos-spoiler" v-model:isOpened="_isOpened" isKeepAlive @dragover.prevent @drop.prevent>
    <template #heading>
      <!-- Header -->
      <div class="header" style="width: 100%; margin-top: 2px">
        <h4
          class="global-h4 header__title"
          v-html="
            props.searchText && includesCaseInsensitive(props.title, props.searchText)
              ? props.title.replace(new RegExp(escapeSpecialSymbols(props.searchText), 'i'), match => `<b>${match}</b>`)
              : props.title
          "
          :id="props.uploadCampaignId || props.myUploadsGroupId || props.locationId || props.creatorVisitId || `photoSpoiler-${props.index}`"
        ></h4>

        <LinkWithIcon v-if="props.isMyUploads" class="header__edit-icon" @click.stop="openEditModal(false)">
          <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
        </LinkWithIcon>

        <LinkWithIcon v-if="props.isMyUploads && !props.locationExternalId" class="header__add-location-link" isDottedUnderline @click.stop="openEditModal(true)">
          <span>Add location</span>
        </LinkWithIcon>

        <RouterLink v-if="props.nonLinkText && props.linkRoute" class="header__link-snippet" target="_blank" :to="props.linkRoute">
          <!--<IconEmbedded name="user_2" :size="21" style="margin-right: 3px" color="rgba(0, 0, 0, 0.5)" />-->
          <span class="header__link-snippet-link" @click.stop>
            {{ props.nonLinkText || "Go to" }}
            <IconEmbedded name="external-link_2-5" :size="15" style="margin-left: 3px" color="rgba(0, 0, 0, 0.5)" />
          </span>
        </RouterLink>

        <div v-if="props.photoCredit && props.isMyUploads" class="header__photo-credit">(Photo Credit: {{ props.photoCredit }})</div>

        <EditTitleAndCommentModal
          v-if="isEditTitleAndCommentModalVisible"
          v-model:isVisible="isEditTitleAndCommentModalVisible"
          :title="props.title"
          :comment="props.additionalInfo"
          :photoCredit="props.photoCredit"
          :customerId="props.customerId"
          :photoGalleryId="props.myUploadsGroupId"
          :locationExternalId="props.locationExternalId"
          :locationName="props.locationName"
          :address="props.address"
          :city="props.city"
          :state="props.state"
          :country="props.country"
          :shouldOpenWithLocationToggle="shouldOpenModalWithLocationToggle"
          @save="upsertedPhotoGallery => emit('update:titleAndComment', upsertedPhotoGallery)"
        />

        <div v-if="props.email" class="header__email">
          <IconEmbedded name="envelope_2" :size="20" style="margin-right: 3px" color="rgba(0, 0, 0, 0.5)" />
          {{ props.email }}
        </div>

        <div v-if="props.photosList?.length" class="header__assets-count">{{ props.photosList.length }} asset{{ props.photosList.length !== 1 ? "s" : "" }}</div>

        <div v-if="props.date" class="header__date">
          <IconEmbedded name="calendar_2" :size="19" style="margin-right: 3px" color="rgba(0, 0, 0, 0.5)" />
          {{ props.date }}
        </div>

        <div style="flex-grow: 1"></div>

        <LinkWithIcon v-if="props.isMyUploads && props.isDeleteGroupButtonVisible" class="header__edit-title-and-comment-link" isDottedUnderline @click.stop="handleDeleteGroupClick">
          <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="16" /></template>
          <span>Delete Group</span>
        </LinkWithIcon>

        <LinkWithIcon v-if="props.isMyUploads" class="header__move-delete-toggle" @click.stop="emit('toggleDeleteOrMoveMode')" :title="props.isDeleteGroupButtonVisible ? 'Done' : 'Move/Delete'">
          <template #icon>
            <IconEmbedded v-if="props.isDeleteGroupButtonVisible" name="check_4" :size="15" />
            <IconEmbedded v-else name="trashcan-alt_2" :size="16" />
          </template>
        </LinkWithIcon>

        <DeleteGroupConfirmationModal
          v-if="isDeleteGroupConfirmModalVisible"
          v-model:isVisible="isDeleteGroupConfirmModalVisible"
          :customerId="props.customerId"
          :groupId="myUploadsGroupId"
          :allGroupFiles="photoSubGroupsList.map(sg => sg.photos).flat()"
          :currentOperationSource="props.currentOperationSource"
          @deletionCompleted="emit('deleteGroup')"
          @deletionFailed404="emit('groupDeletionFailed404')"
        />

        <LinkWithIcon
          v-if="['adventure', 'community-upload', 'location'].includes(props.groupingType) && getContextIdForDownload()"
          class="header__download-all-link"
          isDottedUnderline
          @click.stop="() => requestAssetsBulkDownload()"
          :isDisabled="getContextIdForDownload() === contextIdThatBeingProcessed && isDownloadRequestInProgress"
        >
          <template #icon>
            <LoadingIcon v-if="getContextIdForDownload() === contextIdThatBeingProcessed && isDownloadRequestInProgress" :size="17" />
            <IconEmbedded v-else name="download_2-5" :size="17" color="rgba(0, 0, 0, 0.5)" />
          </template>
          <span>Download all</span>
        </LinkWithIcon>

        <LinkWithIcon v-if="hasContentSummary" class="header__ai-summary-link" isDottedUnderline @click.stop="showAISummary" color="blue">
          <span>AI Summary</span>
        </LinkWithIcon>
      </div>
      <!-- / Header -->

      <CompilingZIPMsgModal
        v-if="isDownloadRequestInfoModalVisible"
        v-model:isVisible="isDownloadRequestInfoModalVisible"
        :customerId="props.customerId"
        :contextId="contextIdThatBeingProcessed"
        :collabInputId="props.creatorVisitId"
        :uploadCampaignId="props.uploadCampaignId"
        :uploaderEmail="uploaderEmailThatBeingProcessed"
        :locationExternalId="props.locationId"
      />
    </template>

    <template #details>
      <div style="width: 100%; height: 0; margin: 6px 0 17px; border-bottom: 1px rgba(0, 0, 0, 0.15) solid"></div>

      <!-- Photos spoiler content -->
      <div
        :class="{
          'photos-spoiler-content': true,
          'photos-spoiler-content--collapsed': shouldShowExpandButton && !isContentExpanded,
        }"
      >
        <!-- Location display -->
        <div v-if="props.address && props.locationExternalId" class="location-display photos-spoiler-content__location-display">
          <IconEmbedded name="map-pin_2-5" :size="19" style="margin-right: 5px" color="rgba(0, 0, 0, 0.5)" />
          <span>{{ props.address }}</span>
        </div>
        <!-- / Location display -->

        <!-- Uploads history -->
        <div v-if="props.groupingType !== 'community-upload' && props.additionalInfo" class="uploads-history photos-spoiler-content__uploads-history">
          <ul
            :class="{
              'uploads-history__info-snippets-list': true,
              'uploads-history__info-snippets-list--half-hidden': props.additionalInfo.length > additionalInfoTruncCharLimit && !isAdditionalInfoFullyVisible,
            }"
          >
            <li class="uploads-history__info-snippet">
              <span
                class="uploads-history__info-text"
                v-html="
                  props.searchText && includesCaseInsensitive(props.additionalInfo, props.searchText)
                    ? props.additionalInfo.trim().replace(new RegExp(escapeSpecialSymbols(props.searchText), 'i'), match => `<b>${match}</b>`)
                    : props.additionalInfo.trim()
                "
              ></span>
              <LinkWithIcon v-if="props.isMyUploads" class="uploads-history__edit-icon" @click.stop="openEditModal(false)"
                ><template #icon><IconEmbedded name="pencil_2" :size="16" /></template
              ></LinkWithIcon>
            </li>
          </ul>

          <div v-if="props.additionalInfo.length > additionalInfoTruncCharLimit" class="uploads-history__footer">
            <LinkWithIcon isDottedUnderline iconInset="auto auto auto 2px" style="margin-left: -5px" @click="isAdditionalInfoFullyVisible = !isAdditionalInfoFullyVisible">
              <template #icon>
                <IconEmbedded :name="isAdditionalInfoFullyVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="14" />
              </template>
              <span v-if="isAdditionalInfoFullyVisible">Show less</span>
              <span v-else>Show more</span>
            </LinkWithIcon>
          </div>
        </div>
        <!-- / Uploads history -->

        <!-- "Select all" and "Edit" buttons -->
        <div v-if="props.isWithSelectAllCheckbox && photoSubGroupsList?.length" class="select-all-and-edit-buttons photos-spoiler-content__select-all-and-edit-buttons">
          <div class="select-all-and-edit-buttons__left-side">
            <SrpCheckbox
              v-if="props.isWithSelectAllCheckbox"
              class="select-all-and-edit-buttons__button"
              size="medium"
              @click.prevent="
                () => {
                  isAllChecked = !isAllChecked;
                  emit('checkUncheckAll', isAllChecked);
                }
              "
            >
              <template #input>
                <input type="checkbox" :checked="isAllChecked" style="pointer-events: none" />
              </template>
              <template #text>Select all</template>
            </SrpCheckbox>
            <slot name="custom-buttons"></slot>
          </div>
        </div>
        <!-- / "Select all" and "Edit" buttons -->

        <!-- Upload area overlay -->
        <div class="upload-area-overlay photos-spoiler-content__upload-area-overlay" @mouseenter="() => (isAddFilesButtonHovered = true)" @mouseleave="() => (isAddFilesButtonHovered = false)">
          <UploadPhotoForm
            ref="uploadPhotoFormRef"
            class="upload-area-overlay__upload-area"
            callerName="contentLibrary"
            flow="customer-upload"
            :img="[]"
            :isFilesLimitReached="isFilesLimitReached"
            :maxNumberOfFilesToUploadAtOnce="maxAssetsPerGroup"
            :beforeUploadCheck="checkAssetLimitBeforeUpload"
            @uploadingStarted="
              () => {
                isUploadingInProgress = true;
                areFileUploadsComplete = false;
                isContentExpanded = true;
              }
            "
            @imageUploadedToServer="
              (uploadedImage: UploadedImage) => {
                uploadedImageIds.push(uploadedImage.serverId);
                emit('assetUploaded', uploadedImage.serverId);

                batchSaveQueue.enqueue(uploadedImage.serverId);
              }
            "
            @uploadProgressChange="
              ({ isUploadInProgress }) => {
                if (!isUploadInProgress) {
                  handleUploadingEnd();
                }
              }
            "
            @error="handleUploadingEnd"
            hideVideoGuidance
            :isVideoUploadAllowed="props.isVideoUploadAllowed"
            :inputId="`upload-form-${props.myUploadsGroupId}`"
          />
        </div>
        <!-- / Upload area overlay -->

        <div
          v-if="!photoSubGroupsList?.length"
          class="thumbnails-sub-group photos-spoiler-content__thumbnails-sub-group"
          :class="{ 'thumbnails-sub-group--drag-over': isDragOverDropZone && props.isMyUploads }"
          @dragover.prevent="props.isMyUploads ? handleDragOver($event) : null"
          @dragleave="props.isMyUploads ? handleDragLeave($event) : null"
          @drop.prevent="props.isMyUploads ? handleDrop($event) : null"
        >
          <ul class="thumbnails-sub-group__thumbnails-list">
            <!-- Add new files button -->
            <li
              v-if="props.isMyUploads"
              :class="{
                'add-new-files-button': true,
                'add-new-files-button--hover': isAddFilesButtonHovered,
                'add-new-files-button--disabled': props.isSelectThumbnailsMode || isUploadingInProgress,
                'thumbnails-sub-group__thumbnail-container': true,
              }"
            >
              <label class="add-new-files-button__upload-photo-form-wrap" :for="`upload-form-${props.myUploadsGroupId}`">
                <span class="add-new-files-button__upload-photo-form"></span>
              </label>

              <template v-if="isUploadingInProgress">
                <LoadingIcon class="add-new-files-button__icon" :size="32" color="rgba(0, 0, 0, 0.5)" />
                <div class="add-new-files-button__text add-new-files-button__text--no-underline">Uploading<EllipsisAnimated /></div>
              </template>

              <template v-else>
                <IconEmbedded class="add-new-files-button__icon" name="upload_2" :size="32" color="rgba(0, 0, 0, 0.5)" />
                <div class="add-new-files-button__text">Upload</div>
              </template>
            </li>
            <!-- / Add new files button -->

            <!-- Spacers are needed to align the last row to the left edge while keeping all tiles horizontally centered at the same time (it's not possible to achieve the same effect with CSS alone) -->
            <li class="thumbnails-sub-group__thumbnail-spacer" v-for="_ in 10" :key="_"></li>
          </ul>
        </div>

        <template v-for="(subGroup, _index) in visibleSubGroupsWhenCollapsed" :key="_index">
          <!-- Thumbnails Sub Group -->
          <div
            v-if="
              !props.searchText ||
              includesCaseInsensitive(props.title, props.searchText) ||
              includesCaseInsensitive(subGroup.title, props.searchText) ||
              includesCaseInsensitive(subGroup.additionalInfo, props.searchText) ||
              includesCaseInsensitive(props.additionalInfo, props.searchText)
            "
            class="thumbnails-sub-group photos-spoiler-content__thumbnails-sub-group"
            :class="{ 'thumbnails-sub-group--drag-over': isDragOverDropZone && props.isMyUploads }"
            @dragover.prevent="props.isMyUploads ? handleDragOver($event) : null"
            @dragleave="props.isMyUploads ? handleDragLeave($event) : null"
            @drop.prevent="props.isMyUploads ? handleDrop($event) : null"
          >
            <!--data-viewport-culling-root-margin="100px"-->
            <!--:data-root-container-query-for-culling="props.rootContainerQueryForCulling"-->
            <!--v-viewport-culling="-->
            <!--isVisible => {-->
            <!--if (isVisible) visibleSubGroupIndexes.push(index);-->
            <!--else visibleSubGroupIndexes = visibleSubGroupIndexes.filter(i => i !== index);-->
            <!--}-->
            <!--"-->
            <!-- Sub Group title -->
            <div v-if="subGroup.title" class="sub-group-title thumbnails-sub-group__title">
              <div class="sub-group-title__content-wrap">
                <IconEmbedded v-if="subGroup.type === 'byLocation'" class="sub-group-title__icon" name="map-pin_2-5" :size="19" color="rgba(0, 0, 0, 0.5)" />
                <IconEmbedded v-if="subGroup.type === 'byAdventure'" class="sub-group-title__icon" name="road-sign_2-5" :size="20" color="rgba(0, 0, 0, 0.5)" />
                <IconEmbedded v-if="subGroup.type === 'byUploadCampaign'" class="sub-group-title__icon" name="upload_2-5" :size="20" color="rgba(0, 0, 0, 0.5)" />
                <IconEmbedded v-if="subGroup.type === 'byCreator'" class="sub-group-title__icon" name="user_2-5" :size="21" color="rgba(0, 0, 0, 0.5)" />

                <div class="sub-group-title__type-and-name">
                  <div class="sub-group-title__type">
                    {{
                      {
                        byLocation: "Location",
                        byAdventure: "Creator Visit",
                        byUploadCampaign: "Upload Campaign",
                        byCreator: groupingType === "community-upload" ? "Uploader" : "Creator",
                      }[subGroup.type]
                    }}:
                  </div>
                  <RouterLink
                    v-if="groupingType === 'location' && subGroup.type === 'byCreator'"
                    class="sub-group-title__name sub-group-title__name--link"
                    :to="{
                      name: 'CreatorProfilePublic',
                      params: { creatorId: subGroup.id },
                    }"
                  >
                    <span
                      v-html="
                        props.searchText && includesCaseInsensitive(subGroup.title, props.searchText)
                          ? subGroup.title.replace(new RegExp(escapeSpecialSymbols(props.searchText), 'i'), match => `<b>${match}</b>`)
                          : subGroup.title
                      "
                    >
                    </span>
                  </RouterLink>
                  <div v-else class="sub-group-title__name">
                    <span
                      v-html="
                        props.searchText && includesCaseInsensitive(subGroup.title, props.searchText)
                          ? subGroup.title.replace(new RegExp(escapeSpecialSymbols(props.searchText), 'i'), match => `<b>${match}</b>`)
                          : subGroup.title
                      "
                    >
                    </span>
                  </div>
                </div>
              </div>

              <LinkWithIcon
                v-if="['community-upload'].includes(props.groupingType)"
                class="sub-group-title__download-all-link"
                isDottedUnderline
                @click.stop="() => requestAssetsBulkDownload(subGroup.id)"
                :isDisabled="getContextIdForDownload(subGroup.id) === contextIdThatBeingProcessed && isDownloadRequestInProgress"
              >
                <template #icon>
                  <LoadingIcon v-if="getContextIdForDownload(subGroup.id) === contextIdThatBeingProcessed && isDownloadRequestInProgress" :size="17" />
                  <IconEmbedded v-else name="download_2-5" :size="17" color="rgba(0, 0, 0, 0.5)" />
                </template>
                <span style="white-space: nowrap">Download all</span>
              </LinkWithIcon>
            </div>
            <!-- / Sub Group title -->

            <SubGroupAdditionalInfo v-if="subGroup.additionalInfo" class="thumbnails-sub-group__additional-info" :text="subGroup.additionalInfo" :searchTerm="props.searchText" />

            <ul class="thumbnails-sub-group__thumbnails-list">
              <!-- Add new files button -->
              <li
                v-if="props.isMyUploads"
                :class="{
                  'add-new-files-button': true,
                  'add-new-files-button--hover': isAddFilesButtonHovered,
                  'add-new-files-button--disabled': props.isSelectThumbnailsMode || isUploadingInProgress,
                  'thumbnails-sub-group__thumbnail-container': true,
                }"
              >
                <label class="add-new-files-button__upload-photo-form-wrap" :for="`upload-form-${props.myUploadsGroupId}`">
                  <span class="add-new-files-button__upload-photo-form"></span>
                </label>

                <template v-if="isUploadingInProgress">
                  <LoadingIcon class="add-new-files-button__icon" :size="32" color="rgba(0, 0, 0, 0.5)" />
                  <div class="add-new-files-button__text add-new-files-button__text--no-underline">Uploading<EllipsisAnimated /></div>
                </template>

                <template v-else>
                  <IconEmbedded class="add-new-files-button__icon" name="upload_2" :size="32" color="rgba(0, 0, 0, 0.5)" />
                  <div class="add-new-files-button__text">Upload</div>
                </template>
              </li>
              <!-- / Add new files button -->

              <!-- Thumbnail container -->
              <li
                class="thumbnail-container thumbnails-sub-group__thumbnail-container"
                v-for="fileName in subGroup.photos.filter(f => (props.areVideoThumbnailsVisible ? f : !FileUtils.isVideoFileType(f)))"
                :key="fileName"
              >
                <ButtonIconExpandable
                  v-if="props.isThumbnailsWithDownloadButton && !props.isSelectThumbnailsMode"
                  class="thumbnail-container__button-icon thumbnail-container__button-icon--top-right"
                  :icons="['download_2-5', 'download_2']"
                  :iconSizes="[16, 16]"
                  @click="emit('downloadImage', fileName)"
                />

                <SrpFileThumbnail
                  class="thumbnail-container__thumbnail"
                  :fileName="fileName"
                  :thumbSize="'thumb-tiny'"
                  :galleryPostfix="String(_index)"
                  :isEntireAreaClickable="props.isThumbnailsWithCheckboxes || props.isSelectThumbnailsMode ? false : props.isThumbnailsEntireAreaClickable"
                  :isEntireAreaCheckable="props.isThumbnailsEntireAreaCheckable"
                  :isWithLoader="false"
                >
                  <template v-if="props.isThumbnailsWithCheckboxes || props.isSelectThumbnailsMode" #default>
                    <input type="checkbox" :checked="props.checkedFileIds.includes(fileName)" @change="$event => checkUncheckAsset($event, fileName, subGroup.photos)" />
                  </template>
                </SrpFileThumbnail>
              </li>
              <!-- / Thumbnail container -->

              <!-- Spacers are needed to align the last row to the left edge while keeping all tiles horizontally centered at the same time (it's not possible to achieve the same effect with CSS alone) -->
              <li class="thumbnails-sub-group__thumbnail-spacer" v-for="_ in 10" :key="_"></li>
            </ul>
          </div>
          <!-- / Thumbnails Sub Group -->
        </template>

        <!-- Show all overlay -->
        <div v-if="shouldShowExpandButton && !isContentExpanded" class="show-all-overlay photos-spoiler-content__show-all-overlay">
          <div class="show-all-overlay__gradient"></div>
          <LinkWithIcon class="show-all-overlay__button" isDottedUnderline iconInset="1px auto auto 2px" @click="isContentExpanded = true">
            <template #icon><IconEmbedded name="caret-bottom_4" :size="14" /></template>
            <span>Show all</span>
          </LinkWithIcon>
        </div>
        <!-- / Show all overlay -->
      </div>
      <!-- / Photos spoiler content -->
    </template>
  </SrpDetailsSummary>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch, inject } from "vue";
import { useRoute } from "vue-router";
import { escapeSpecialSymbols, includesCaseInsensitive } from "@logic/String";
import AssetsDownloadRequestRepo from "@repos/assetsDownloadRequestRepo";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import FileDownload from "@logic/FileDownload";
import FileUtils from "@logic/FileUtils";
import lodashReverse from "lodash-es/reverse";
import BatchSaveQueue from "@logic/BatchSaveQueue";
import { useMultiselectWithShift } from "@composables/useMultiselectWithShift";
import { useToastsStore } from "@stores/toasts";
import { MetricSender } from "@helpers/MetricSender";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SubGroupAdditionalInfo from "@components/AllPhotosSection/SubGroupAdditionalInfo.vue";
import ButtonIconExpandable from "@components/ui/ButtonIconExpandable.vue";
import UploadPhotoForm from "@views/CMS/UploadPhotoForm.vue";
import EditTitleAndCommentModal from "./EditTitleAndCommentModal.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";
import DeleteGroupConfirmationModal from "@components/AllPhotosSection/DeleteGroupConfirmationModal.vue";
import CompilingZIPMsgModal from "@components/Modals/CompilingZIPMsgModal.vue";

// Types
import {
  SubGroupingType,
  PhotoSubGroup,
  LocationShortPhotoSummary,
  AdventureShortPhotoSummary,
  UploadCampaignShortPhotoSummary,
  CreatorShortPhotoSummary,
  GroupingType,
  PhotoGalleryCustomerData,
  PhotoGallerySource,
  MAX_ASSETS_PER_GROUP,
} from "@contracts/photosPageData";
import { UploadedImage } from "@contracts/uploadedImage";
import { ContentSummaryData } from "@/contracts/contentCalendar";
import { MultiselectItemIdAndStatus } from "@composables/useMultiselectWithShift";

const route = useRoute();

// Constants for collapsed state
// NOTE: This makes a bunch of assumptions about the style sizes.
// const VISIBLE_ROWS = Math.ceil(COLLAPSED_HEIGHT_PX / THUMBNAIL_HEIGHT_WITH_MARGIN); // ~2 rows
// const MAX_VISIBLE_COLLAPSED = VISIBLE_ROWS * IMAGES_PER_ROW_ESTIMATE;
// Just hardcoding this now (could optimize later)
const MAX_VISIBLE_COLLAPSED = 16; // 2 rows of 8 per row (on desktop)

// This limit is for uploading (not rendering)
// Can be overridden by route query param for testing
const maxAssetsPerGroup = ref<number>(MAX_ASSETS_PER_GROUP);

const props = withDefaults(
  defineProps<{
    title: string | null;
    date?: string | null;
    email?: string | null;
    additionalInfo?: string | null;
    nonLinkText?: string | null;
    linkRoute?: any | null;
    isThumbnailsWithCheckboxes?: boolean;
    isThumbnailsWithDownloadButton?: boolean;
    isWithSelectAllCheckbox?: boolean;
    isMyUploads?: boolean;
    photosList: Array<string> | null;
    checkedFileIds: Array<string> | null;
    isThumbnailsEntireAreaClickable?: boolean;
    isThumbnailsEntireAreaCheckable?: boolean;
    groupingType?: GroupingType | null;
    subGroupingTypes?: Array<SubGroupingType> | null;
    locationShortPhotoSummariesList?: Array<LocationShortPhotoSummary> | null;
    adventureShortPhotoSummariesList?: Array<AdventureShortPhotoSummary> | null;
    uploadCampaignShortPhotoSummariesList?: Array<UploadCampaignShortPhotoSummary> | null;
    creatorShortPhotoSummariesList?: Array<CreatorShortPhotoSummary> | null;
    searchText?: string | null;
    rootContainerQueryForCulling?: string | null;
    defaultOpened?: boolean;
    myUploadsGroupId?: string | null;
    isDeleteGroupButtonVisible?: boolean;
    isSelectThumbnailsMode?: boolean;
    customerId: string;
    creatorVisitId?: string | null;
    uploadCampaignId?: string | null;
    locationId?: string | null;
    contentSummaryData?: Array<ContentSummaryData> | null;
    index: number;
    isVideoUploadAllowed?: boolean;
    areVideoThumbnailsVisible?: boolean;
    saveFunction?: (assetIds: Array<string>) => Promise<void>;
    currentOperationSource?: PhotoGallerySource | null;
    // Location Id and name manually attached to My Uploads (note the locationId above is for the Locations tab)
    locationExternalId?: string | null;
    locationName?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    photoCredit?: string | null;
  }>(),
  {
    title: null,
    date: null,
    email: null,
    additionalInfo: null,
    nonLinkText: null,
    linkRoute: null,
    isThumbnailsWithCheckboxes: false,
    isThumbnailsWithDownloadButton: false,
    isWithSelectAllCheckbox: false,
    isMyUploads: false,
    photosList: () => [],
    checkedFileIds: () => [],
    isThumbnailsEntireAreaClickable: true,
    isThumbnailsEntireAreaCheckable: false,
    groupingType: null,
    subGroupingTypes: null,
    locationShortPhotoSummariesList: null,
    adventureShortPhotoSummariesList: null,
    uploadCampaignShortPhotoSummariesList: null,
    creatorShortPhotoSummariesList: null,
    searchText: null,
    rootContainerQueryForCulling: null,
    defaultOpened: true,
    myUploadsGroupId: null,
    isDeleteGroupButtonVisible: false,
    isSelectThumbnailsMode: false,
    customerId: "",
    creatorVisitId: null,
    uploadCampaignId: null,
    locationId: null,
    contentSummaryData: null,
    isVideoUploadAllowed: true,
    areVideoThumbnailsVisible: true,
    saveFunction: (assetIds: Array<string>) => Promise.resolve(),
    currentOperationSource: null,
    locationExternalId: null,
    locationName: null,
    address: null,
    city: null,
    state: null,
    country: null,
    photoCredit: null,
  }
);

const emit = defineEmits<{
  (e: "downloadImage", value: string): void;
  (e: "checkUncheckFile", fileName: string, isChecked: boolean): void;
  (e: "checkUncheckAll", isChecked: boolean): void;
  (e: "uploadMore", imageIds: Array<string>): void;
  (e: "update:titleAndComment", upsertedPhotoGallery: PhotoGalleryCustomerData): void;
  (e: "deleteGroup"): void;
  (e: "groupDeletionFailed404"): void;
  (e: "showAISummary", contentSummary: string): void;
  (e: "assetUploaded", assetId: string): void;
  (e: "uploadStateChanged", payload: { groupId: string; isUploading: boolean }): void;
  (e: "toggleDeleteOrMoveMode"): void;
}>();

// Collapsed/Expanded state for "Show all" feature ===========================
const isContentExpanded = ref<boolean>(false);

// Additional info params =====================================================
const isAdditionalInfoFullyVisible = ref<boolean>(false);
const additionalInfoTruncCharLimit = 250;

// Toggle the "Edit Title and Comment" modal ==================================
const isEditTitleAndCommentModalVisible = ref<boolean>(false);
const shouldOpenModalWithLocationToggle = ref<boolean>(false);

function openEditModal(withLocationToggle: boolean) {
  shouldOpenModalWithLocationToggle.value = withLocationToggle;
  isEditTitleAndCommentModalVisible.value = true;
}

// Toggle the "Delete group confirmation" modal ===============================
const isDeleteGroupConfirmModalVisible = ref<boolean>(false);

// Handle delete group click ==================================================
async function handleDeleteGroupClick() {
  if (props.photosList && props.photosList.length > 0) {
    // Show confirmation modal if there are items
    isDeleteGroupConfirmModalVisible.value = true;
  } else {
    // Directly delete if no items
    await PhotoGalleryV2Repo.deleteMyUploadsGroup(props.customerId, props.myUploadsGroupId, props.currentOperationSource);
    emit("deleteGroup");
  }
}

// Content Summary ============================================================
function getAISummary(item: ContentSummaryData | null | undefined): string | undefined {
  if (!item) return undefined;
  return item.excludeFromIndex?.aiSummary;
}

const hasContentSummary = computed(() => {
  if (!props.contentSummaryData) return false;

  // Find matching content based on context type and ID
  const matchingContent = findMatchingContent();
  return !!getAISummary(matchingContent);
});

const contextId = computed<string>(() => {
  let contextId: string | null = null;

  // Map grouping type to context type and get context ID
  if (props.groupingType === "adventure" && props.creatorVisitId) {
    contextId = props.creatorVisitId;
  } else if (props.groupingType === "community-upload" && props.uploadCampaignId) {
    contextId = props.uploadCampaignId;
  } else if (props.groupingType === "my-uploads" && props.myUploadsGroupId) {
    contextId = props.myUploadsGroupId;
  } else if (props.groupingType === "location" && props.locationId) {
    contextId = props.locationId;
  }

  return contextId;
});

const contextType = computed<string>(() => {
  let contextType: string | null = null;

  // Map grouping type to context type and get context ID
  if (props.groupingType === "adventure" && props.creatorVisitId) {
    contextType = "CreatorVisit";
  } else if (props.groupingType === "community-upload" && props.uploadCampaignId) {
    contextType = "CommunityUpload";
  } else if (props.groupingType === "my-uploads" && props.myUploadsGroupId) {
    contextType = "CustomerUpload";
  } else if (props.groupingType === "location" && props.locationId) {
    contextType = "Location";
  }

  return contextType;
});

const findMatchingContent = (): ContentSummaryData | null => {
  if (!props.contentSummaryData) return null;

  if (contextType.value && contextId.value) {
    return props.contentSummaryData.find(item => item.contextType === contextType.value && item.contextId === contextId.value);
  }

  return null;
};

// AI Summary =================================================================
const showAISummary = () => {
  const matchingContent = findMatchingContent();
  const aiSummary = getAISummary(matchingContent);
  if (aiSummary) {
    emit("showAISummary", aiSummary);
  }
};

// Make Photo Sub Groups ======================================================
function createSubGroup(
  photosList: Array<string>,
  summary: {
    locationId?: string;
    locationName?: string;

    adventureId?: string;
    adventureName?: string;

    campaignId?: string;
    campaignName?: string;

    photos: Array<string>;
    additionalInfo?: string;
  },
  subGroupingType: SubGroupingType
): PhotoSubGroup {
  const keyWord = {
    byLocation: "location",
    byAdventure: "adventure",
    byUploadCampaign: "uploadCampaign",
    byCreator: "creator",
  }[subGroupingType];

  const subGroup = {
    // The location id is sometimes null (for an unknown reason), so it can't be used as a reconciliation key
    id: summary[`${keyWord}Id`],
    type: subGroupingType,
    title: summary[`${keyWord}Name`],
    photos: [],
    additionalInfo: summary.additionalInfo,
  } as PhotoSubGroup;

  summary.photos.forEach(fileName => {
    if (props.photosList.includes(fileName)) {
      subGroup.photos.push(fileName);
      // Delete the file from the local copy of the _photosList
      const justSubGroupedFileIndex = photosList.findIndex(f => f === fileName);
      if (justSubGroupedFileIndex !== -1) {
        photosList.splice(justSubGroupedFileIndex, 1);
      }
    }
  });

  return subGroup;
}

const photoSubGroupsList = computed<Array<PhotoSubGroup>>(() => {
  const _photoSubGroupsList = [];
  const _photosList = [...props.photosList];

  if (props.subGroupingTypes?.includes("byLocation") && props.locationShortPhotoSummariesList?.length) {
    props.locationShortPhotoSummariesList.forEach(summary => {
      _photoSubGroupsList.push(createSubGroup(_photosList, summary, "byLocation"));
    });
  }

  if (props.subGroupingTypes?.includes("byAdventure") && props.adventureShortPhotoSummariesList?.length) {
    props.adventureShortPhotoSummariesList.forEach(summary => {
      _photoSubGroupsList.push(createSubGroup(_photosList, summary, "byAdventure"));
    });
  }

  if (props.subGroupingTypes?.includes("byUploadCampaign") && props.uploadCampaignShortPhotoSummariesList?.length) {
    props.uploadCampaignShortPhotoSummariesList.forEach(summary => {
      _photoSubGroupsList.push(createSubGroup(_photosList, summary, "byUploadCampaign"));
    });
  }

  if (props.subGroupingTypes?.includes("byCreator") && props.creatorShortPhotoSummariesList?.length) {
    props.creatorShortPhotoSummariesList.forEach(summary => {
      _photoSubGroupsList.push(createSubGroup(_photosList, summary, "byCreator"));
    });
  }

  // Put files that left into the "ungrouped" subgroup (after location/other subgroups so visit cover etc. appear below Location sections)
  // Note! Ungrouped used to be at the top. Initially it was so highlights show up top but unless we show them twice this top section was really slim.
  if (_photosList.length) {
    _photoSubGroupsList.push({
      id: 0,
      type: null,
      title: null,
      photos: _photosList,
    });
  }

  // Filter out empty groups
  return _photoSubGroupsList.filter(g => g.photos.length > 0) as Array<PhotoSubGroup>;
});

// Collapsed state computed properties ========================================
// Count total photos across all subgroups (excluding videos if needed)
const totalPhotoCount = computed<number>(() => {
  return photoSubGroupsList.value.reduce((total, subGroup) => {
    const filteredPhotos = subGroup.photos.filter(f => (props.areVideoThumbnailsVisible ? f : !FileUtils.isVideoFileType(f)));
    return total + filteredPhotos.length;
  }, 0);
});

// Determine if we should show the "Show all..." button
const shouldShowExpandButton = computed<boolean>(() => {
  // Account for the upload button in My Uploads (adds 1 to the count)
  const uploadButtonCount = props.isMyUploads && photoSubGroupsList.value.length > 0 ? 1 : 0;
  return totalPhotoCount.value + uploadButtonCount > MAX_VISIBLE_COLLAPSED;
});

// Get the subgroups with photos limited when collapsed
const visibleSubGroupsWhenCollapsed = computed<Array<PhotoSubGroup>>(() => {
  if (isContentExpanded.value || !shouldShowExpandButton.value) {
    return photoSubGroupsList.value;
  }

  // When searching, return full list so matching subgroups are visible (otherwise collapsed view shows blank)
  if (props.searchText?.trim()) {
    return photoSubGroupsList.value;
  }

  // When collapsed, limit the total number of visible photos
  const result: Array<PhotoSubGroup> = [];
  let photosRendered = 0;
  const uploadButtonCount = props.isMyUploads ? 1 : 0; // Reserve space for upload button

  for (const subGroup of photoSubGroupsList.value) {
    const filteredPhotos = subGroup.photos.filter(f => (props.areVideoThumbnailsVisible ? f : !FileUtils.isVideoFileType(f)));

    if (photosRendered + uploadButtonCount >= MAX_VISIBLE_COLLAPSED) {
      // We've reached the limit
      break;
    }

    const remainingSlots = MAX_VISIBLE_COLLAPSED - photosRendered - uploadButtonCount;

    if (filteredPhotos.length <= remainingSlots) {
      // Add the entire subgroup
      result.push(subGroup);
      photosRendered += filteredPhotos.length;
    } else {
      // Partially add this subgroup
      const limitedSubGroup = {
        ...subGroup,
        photos: filteredPhotos.slice(0, remainingSlots),
      };
      result.push(limitedSubGroup);
      photosRendered += remainingSlots;
      break;
    }
  }

  return result;
});

// Track subgroups visibility =================================================
const visibleSubGroupIndexes = ref<Array<number>>([]);

// Is Spoiler Opened ==========================================================
const _isOpened = ref<boolean>(props.defaultOpened);

// Open spoiler on tab switch =================================================
watch(
  () => props.groupingType,
  () => (_isOpened.value = true)
);

// Check/uncheck all ==========================================================
const isAllChecked = ref<boolean>(false);

onMounted(() => {
  isAllChecked.value = props.checkedFileIds?.length === props.photosList?.length;

  // Check for maxupload query param and apply if it reduces the limit
  const maxUploadParam = route.query.maxupload;
  if (maxUploadParam) {
    const maxUploadValue = parseInt(maxUploadParam as string, 10);
    if (!isNaN(maxUploadValue) && maxUploadValue < maxAssetsPerGroup.value) {
      maxAssetsPerGroup.value = maxUploadValue;
    }
  }
});

watch(
  () => props.checkedFileIds,
  () => {
    isAllChecked.value = props.checkedFileIds?.length === props.photosList?.length;
  },
  { deep: true }
);

// Upload images and emit the imageIds array ==================================
const uploadedImageIds = ref<Array<string>>([]);
// Tracks if an upload has started
const isUploadingInProgress = ref<boolean>(false);
// Tracks whether the actual image file uploads has completed.
// Both there are needed because the uploads and batch save can happen in parallel
// and it could seem like everything is "done" even when it's not.
const areFileUploadsComplete = ref<boolean>(true);

function handleUploadingEnd() {
  // Mark that file uploads are done
  areFileUploadsComplete.value = true;
  // Only mark upload as complete if batch save queue is also empty
  // This ensures we stay in "uploading" state until all saves are flushed
  if (!batchSaveQueue.hasPendingItems()) {
    isUploadingInProgress.value = false;
  }
  // Otherwise, the periodic check will set it false when queue empties
}

// Periodically check if we can complete the upload state ==================
// This handles the case where file uploads finish but batch save queue still has items
onMounted(() => {
  const intervalId = setInterval(() => {
    // Only mark complete when BOTH file uploads are done AND batch queue is empty
    // This prevents the bug where an empty queue at upload START was incorrectly treated as "done"
    if (isUploadingInProgress.value && areFileUploadsComplete.value && !batchSaveQueue.hasPendingItems()) {
      isUploadingInProgress.value = false;
    }
  }, 1000);
  // Clean up interval when component unmounts
  onBeforeUnmount(() => clearInterval(intervalId));
});

// Watch for upload state changes and emit ====================================
watch(isUploadingInProgress, newVal => {
  if (props.myUploadsGroupId) {
    emit("uploadStateChanged", { groupId: props.myUploadsGroupId, isUploading: newVal });
  }
});

// Request assets bulk download ===============================================
const isDownloadRequestInfoModalVisible = ref<boolean>(false);

const contextIdThatBeingProcessed = ref<string>("");
const uploaderEmailThatBeingProcessed = ref<string>("");
const isDownloadRequestInProgress = ref<boolean>(false);

function getContextIdForDownload(uploaderEmail?: string): string {
  let contextId: string;

  if (props.groupingType === "adventure") {
    contextId = props.creatorVisitId;
  } else if (props.groupingType === "location") {
    contextId = props.locationId;
  } else if (props.groupingType === "community-upload") {
    if (uploaderEmail) {
      contextId = `${props.uploadCampaignId}|${uploaderEmail}`;
    } else {
      contextId = `${props.uploadCampaignId}`;
    }
  }

  return contextId;
}

async function requestAssetsBulkDownload(uploaderEmail?: string): Promise<void> {
  contextIdThatBeingProcessed.value = getContextIdForDownload(uploaderEmail);
  uploaderEmailThatBeingProcessed.value = uploaderEmail || "";
  isDownloadRequestInProgress.value = true;

  const downloadRequest = await AssetsDownloadRequestRepo.LoadByContextId(
    props.customerId,
    contextIdThatBeingProcessed.value,
    true,
    props.creatorVisitId || null,
    props.uploadCampaignId || null,
    uploaderEmail || null,
    props.locationId || null
  );

  if (downloadRequest.completedDate) {
    FileDownload.downloadFileByURI(`${globalThis.Bootstrap.Config.contentBlobBaseUri}/${downloadRequest.downloadFileLocation}`);
    contextIdThatBeingProcessed.value = "";
  } else {
    isDownloadRequestInfoModalVisible.value = true;
  }
  isDownloadRequestInProgress.value = false;
}

// "Add files" button hover state =============================================
const isAddFilesButtonHovered = ref<boolean>(false);

// Drag and drop state ========================================================
const isDragOverDropZone = ref<boolean>(false);
const uploadPhotoFormRef = ref(null);

// Asset limit tracking =======================================================
const toastsStore = useToastsStore();
const globalRemoteLogger = inject("globalRemoteLogger") as any;
const isFilesLimitReached = ref<boolean>(false);

const currentAssetCount = computed<number>(() => {
  return props.photosList?.length || 0;
});

function checkAssetLimitBeforeUpload(newFileCount: number): boolean {
  const totalAfterUpload = currentAssetCount.value + newFileCount;

  if (totalAfterUpload > maxAssetsPerGroup.value) {
    // Warn and log
    toastsStore.warn({
      message: `Max ${maxAssetsPerGroup.value} files per group`,
      details: `Adding ${newFileCount} to ${currentAssetCount.value} existing files will exceed the current limit of ${maxAssetsPerGroup.value}.\nPlease split your upload into multiple groups.`,
      expiresInMs: 30000,
    });
    globalRemoteLogger.info(
      `PhotoGallery AssetLimitExceeded: ${props.customerId} groupId=${props.myUploadsGroupId}, adding=${newFileCount}, currentCount=${currentAssetCount.value}, limit=${maxAssetsPerGroup.value}`,
      false,
      false
    );
    MetricSender.sendMetric("PhotoGallery-AssetLimitExceeded", { currentCount: currentAssetCount.value, adding: newFileCount, limit: maxAssetsPerGroup.value });

    // Upload now allowed
    return false; // Abort upload
  }
  // Upload can proceed
  return true;
}

// Handle Batch Save Queue ====================================================
const batchSaveQueue = new BatchSaveQueue({
  saveFn: async idsArray => {
    await props.saveFunction(lodashReverse(idsArray));
  },
  storageKey: `PhotosSpoiler-${contextId.value}`,
  batchSize: 5,
  maxWaitMs: 2000,
  // eslint-disable-next-line no-console
  onLog: msg => console.info(`PhotoBatchSave: ${msg}`),
});

// Init check/uncheck composable ==============================================
const { defineWhichItemsToCheckUncheck } = useMultiselectWithShift();

function checkUncheckAsset(event: Event, fileName: string, subGroupAssetIds: Array<string>) {
  const result: Array<MultiselectItemIdAndStatus> = defineWhichItemsToCheckUncheck(event, fileName, subGroupAssetIds, props.checkedFileIds);
  result.forEach(r => emit("checkUncheckFile", r.id, r.isChecked));
}

// Drag and drop handlers =====================================================
function handleDragOver(event: DragEvent) {
  event.preventDefault(); // Required to allow drop
  isDragOverDropZone.value = true;
}

function handleDragLeave(event: DragEvent) {
  // Only set to false if leaving the drop zone entirely
  const target = event.currentTarget as HTMLElement;
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (!target || !relatedTarget || !target.contains(relatedTarget)) {
    isDragOverDropZone.value = false;
  }
}

function handleDrop(event: DragEvent) {
  event.preventDefault();
  isDragOverDropZone.value = false;

  // Forward to UploadPhotoForm for processing
  if (uploadPhotoFormRef.value) {
    uploadPhotoFormRef.value.dragFile(event);
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Location display ===========================================================
.location-display {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  color: rgba(91, 91, 91, 1);
  font: 14px/19px sans-serif;
}

// Uploads history ============================================================
.uploads-history {
  color: rgba(91, 91, 91, 1);
  font: 14px/19px sans-serif;

  &__info-snippets-list {
    padding: 0;
    margin: 0;
    position: relative;
    z-index: 0;
    list-style: none;
    overflow: hidden;

    &--half-hidden {
      display: -webkit-box !important;
      overflow: hidden !important;
      line-clamp: 2 !important;
      -webkit-line-clamp: 2 !important;
      text-overflow: ellipsis !important;
      -webkit-box-orient: vertical !important;
      margin-bottom: 10px;

      &::after {
        content: "";
        width: 100%;
        height: 20px;
        position: absolute;
        inset: auto auto 0 0;
        z-index: 1;
        pointer-events: none;
        background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
      }
    }
  }

  &__info-snippet {
    padding-bottom: 8px;
    margin-bottom: 8px;
    position: relative;
    z-index: 0;
    white-space: pre-wrap;

    &::after {
      content: "";
      width: 100%;
      max-width: 250px;
      border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
      position: absolute;
      inset: auto auto 0 0;
    }

    &:last-child {
      padding-bottom: 0;

      &::after {
        display: none;
      }
    }
  }

  &__edit-icon {
    display: inline-block !important;
    margin-left: 0;
    vertical-align: middle;
  }

  &__info-text {
    :deep(b) {
      background: rgba(230, 230, 0, 1);
      outline: 2px rgba(200, 200, 0, 1) solid;
    }
  }

  &__footer {
  }

  &__show-more-link {
  }
}

// "Select all" and "Edit" buttons ============================================
.select-all-and-edit-buttons {
  display: flex;
  justify-content: space-between;

  &__left-side {
    gap: 15px;
    display: flex;
    align-items: center;
  }

  &__right-side {
    gap: 15px;
    display: flex;
    align-items: center;
  }

  &__button {
  }
}

// Thumbnail container ========================================================
.thumbnail-container {
  aspect-ratio: 4/3;
  border-radius: 6px;
  position: relative;
  z-index: 0;
  background: #d7d7d7;

  &__button-icon {
    position: absolute;
    z-index: 1;

    &--top-right {
      inset: 5px 5px auto auto;
    }
  }

  &__thumbnail {
    position: relative;
    z-index: 0;
  }
}

// Sub Group title ============================================================
.sub-group-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 0;

  &::after {
    content: "";
    flex-grow: 1;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__content-wrap {
    margin-right: 12px;
    display: flex;
    align-items: center;
  }

  &__icon {
    margin-right: 3px;
  }

  &__type-and-name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__type {
    margin-right: 8px;
    color: rgba(0, 0, 0, 0.5);
    font-weight: 600;
    line-height: 15px;
  }

  &__name {
    font-weight: 700;
    line-height: 15px;

    :deep(b) {
      background: rgba(230, 230, 0, 1);
      outline: 2px rgba(200, 200, 0, 1) solid;
    }

    &--link {
      text-decoration: underline;
      text-decoration-color: rgba(0, 0, 0, 0.2);
      text-underline-offset: 2px;
      text-decoration-thickness: 1px;
      text-decoration-style: solid;

      &:hover {
        text-decoration: none;
      }
    }
  }

  &__download-all-link {
    margin-right: 10px;
  }
}

// Add new files button =======================================================
.add-new-files-button {
  @include standardFont;
  min-height: 95px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;
  color: rgba(91, 91, 91, 1);
  font-weight: bold;
  cursor: pointer;
  user-select: none;

  &::before {
    content: "";
    width: 100%;
    height: 100%;
    border: 2px rgba(0, 0, 0, 0.25) dashed;
    border-radius: 4px;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
    background: rgba(240, 240, 240, 1);
    transition:
      width 0.07s ease-in-out,
      height 0.07s ease-in-out,
      inset 0.07s ease-in-out,
      background-color 0.07s ease-in-out;
  }

  &:hover::before,
  &--hover::before {
    width: calc(100% + 6px);
    height: calc(100% + 6px);
    inset: -3px auto auto -3px;
    background: rgba(225, 225, 225, 1);
  }

  &--disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  &__upload-photo-form-wrap {
    width: calc(100% + 6px);
    height: calc(100% + 6px);
    position: absolute;
    inset: -3px auto auto -3px;
    z-index: 2;
    overflow: hidden;
  }

  &__upload-photo-form {
    width: 200%;
    height: 200%;
    min-height: 200%;
    max-height: 200%;
    opacity: 0;
  }
}

// Thumbnails Sub Group =======================================================
.thumbnails-sub-group {
  position: relative;

  &--drag-over {
    &::before {
      content: "";
      width: calc(100% + 20px);
      height: calc(100% + 20px);
      border: 3px dashed rgba(5, 133, 135, 0.6);
      border-radius: 8px;
      position: absolute;
      inset: -10px auto auto -10px;
      z-index: 5;
      background: rgba(5, 133, 135, 0.05);
      pointer-events: none;
    }
  }

  &__title {
    margin-bottom: 12px;
  }

  &__additional-info {
    margin-bottom: 12px;
  }

  &__thumbnails-list {
    padding: 0;
    margin: 0 -5px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    list-style: none;
  }

  &__thumbnail-container {
    width: 130px;
    max-width: 130px;
    margin: 5px;
  }

  &__thumbnail-spacer {
    width: 130px;
    max-width: 130px;
    margin: 0 5px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .thumbnails-sub-group {
    &__thumbnail-container,
    &__thumbnail-spacer {
      width: calc(13% + 2px);
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .thumbnails-sub-group {
    &__thumbnail-container,
    &__thumbnail-spacer {
      width: calc(15% + 3px);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .thumbnails-sub-group {
    &__thumbnail-container,
    &__thumbnail-spacer {
      width: calc(15% + 2px);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .thumbnails-sub-group {
    &__thumbnail-container,
    &__thumbnail-spacer {
      width: calc(23%);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .thumbnails-sub-group {
    &__thumbnail-container,
    &__thumbnail-spacer {
      width: calc(33.3% - 10px);
    }
  }
}

// Upload area overlay ========================================================
.upload-area-overlay {
  width: 130px;
  height: 100px;
  position: relative;
  z-index: 0;
  overflow: hidden;
  opacity: 0;

  &--disabled {
    pointer-events: none;
  }

  &__label {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 3;
    cursor: pointer;
  }

  &__upload-area {
    width: 100%;
    height: 100%;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .upload-area-overlay {
    width: 30dvw;
  }
}

// Photos spoiler content =====================================================
.photos-spoiler-content {
  position: relative;
  z-index: 0;

  &--collapsed {
    max-height: 160px;
    overflow: hidden;
  }

  &__header {
  }

  &__uploads-history {
    width: 100%;
    // margin-bottom: 10px;
  }

  &__select-all-and-edit-buttons {
    margin-bottom: 20px;
  }

  &__upload-area-overlay {
    position: absolute;
    z-index: 4;
  }

  &__thumbnails-sub-group {
    margin-bottom: 25px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__show-all-overlay {
    // Positioned at the bottom of the container
  }
}

// Show all overlay ===========================================================
.show-all-overlay {
  width: 100%;
  height: 50px;
  position: absolute;
  inset: auto auto 0 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  pointer-events: none;

  &__gradient {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.9) 30%, rgba(255, 255, 255, 1) 50%);
  }

  &__button {
    margin-bottom: 7px;
    position: relative;
    z-index: 10;
    pointer-events: auto;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .photos-spoiler-content {
    &__upload-area-overlay {
      right: calc(50% + 15dvw);
    }
  }
}

// Header =====================================================================
.header {
  gap: 15px 30px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  color: rgba(91, 91, 91, 1);
  font: 14px/19px sans-serif;

  &__title {
    :deep(b) {
      background: rgba(230, 230, 0, 1);
      outline: 2px rgba(200, 200, 0, 1) solid;
    }
  }
  &:hover &__title {
    text-decoration: none;
  }

  &__edit-icon {
    margin-left: -15px;
  }

  &__add-location-link {
    margin-left: -15px;
  }

  &__download-all-link {
    content: "";
  }

  &__link-snippet,
  &__email,
  &__assets-count,
  &__date,
  &__photo-credit {
    display: flex;
    align-items: center;
  }

  &__assets-count {
    padding-top: 2px;
  }

  &__link-snippet-link {
    display: flex;
    align-items: center;
    text-decoration: underline;
    text-decoration-color: rgba(91, 91, 91, 0.5);
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
    text-decoration-style: dashed;

    &:hover {
      text-decoration: none;
    }
  }
}

// Photos spoiler =============================================================
.photos-spoiler {
  padding: 17px 0 24px;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 50px);
    height: 100%;
    box-sizing: border-box;
    border: 1px rgba(0, 0, 0, 0.15) solid;
    border-radius: 6px;
    position: absolute;
    inset: 0 auto auto -25px;
  }
}
</style>
